/**
 * masudajuken: 途切れずループするスライドショー（TOP のヒーロー用）。
 *
 * 本家 Horizon の slideshow-component とは切り離して動く。スクロールと
 * scroll-snap を使わず、トラックを transform で1枚分動かしてから
 * 「先頭のスライドを末尾へ回す（戻るときは逆）」だけで無限ループを作る。
 * 位置は常に「トラックの lead 番目の子 = 現在のスライド」で、その子の中心が
 * 画面の中心に来るようトラック全体をずらしておく。lead 枚は左側を埋めるための
 * 控え。スクロール位置と現在番号がずれる種類の不具合が起きない。
 *
 * 左右の端まで埋めるのに枚数が足りないときだけ、スライド一式を丸ごと複製して
 * 足す（aria-hidden・tabindex=-1。見えるのでクリックは生かす）。一式単位で
 * 並ぶので、何枚回しても並び順の周期は崩れない。
 */

const SWIPE_THRESHOLD = 40;
const DRAG_CLICK_TOLERANCE = 8;

class LoopCarousel extends HTMLElement {
  /** @type {HTMLElement} */ #viewport;
  /** @type {HTMLElement} */ #track;
  /** @type {HTMLButtonElement[]} */ #dots = [];
  #count = 0;
  /** 現在のスライドより左に置いておく枚数 */
  #lead = 0;
  #busy = false;
  #paused = false;
  #hovered = false;
  #focused = false;
  #inView = true;
  /** @type {number | undefined} */ #timer;
  /** @type {ResizeObserver | undefined} */ #resizeObserver;
  /** @type {IntersectionObserver | undefined} */ #intersectionObserver;
  /** @type {{ x: number, y: number, dx: number, id: number, horizontal: boolean | null } | null} */ #drag = null;
  #suppressClick = false;
  #lastWidth = 0;

  connectedCallback() {
    const viewport = this.querySelector('.loop-carousel__viewport');
    const track = this.querySelector('.loop-carousel__track');
    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement)) return;
    this.#viewport = viewport;
    this.#track = track;

    const slides = this.#realSlides();
    this.#count = slides.length;
    slides.forEach((slide, index) => {
      slide.dataset.loopIndex = String(index);
      slide.setAttribute('aria-hidden', 'false');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} / ${slides.length}`);
    });

    this.#dots = [...this.querySelectorAll('[data-loop-dot]')].filter(
      (el) => el instanceof HTMLButtonElement
    );

    if (this.#count < 2) return;

    this.#fill();
    this.#update();
    this.#bind();
    this.#restartTimer();
    this.setAttribute('ready', '');
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#resizeObserver?.disconnect();
    this.#intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  /** 現在のスライド番号（0 始まり）。トラックの lead 番目の子がそのまま現在地。 */
  get current() {
    const el = this.#track.children[this.#lead];
    return el instanceof HTMLElement ? Number(el.dataset.loopIndex) || 0 : 0;
  }

  get #duration() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;
    return Number(this.getAttribute('duration')) || 600;
  }

  get #interval() {
    return (Number(this.getAttribute('autoplay')) || 0) * 1000;
  }

  next() {
    this.#move(1);
  }

  previous() {
    this.#move(-1);
  }

  /** @param {number} index */
  goTo(index) {
    const n = this.#count;
    const forward = (((index - this.current) % n) + n) % n;
    if (forward === 0) return;
    const delta = forward <= n / 2 ? forward : forward - n;
    this.#move(delta);
  }

  pause() {
    this.#paused = true;
    this.toggleAttribute('paused', true);
  }

  play() {
    this.#paused = false;
    this.toggleAttribute('paused', false);
    this.#restartTimer();
  }

  /* ---------- 動き ---------- */

  #realSlides() {
    return [...this.#track.children].filter(
      (el) => el instanceof HTMLElement && !el.hasAttribute('data-loop-clone')
    );
  }

  /** 1枚分の移動量。隣り合う2枚の左端の差なので gap も含む。 */
  #step() {
    const [a, b] = this.#track.children;
    if (!a || !b) return 0;
    return b.getBoundingClientRect().left - a.getBoundingClientRect().left;
  }

  /**
   * @param {number} px
   * @param {boolean} animate
   */
  #setOffset(px, animate) {
    const duration = animate ? this.#duration : 0;
    this.#track.style.transition = duration ? `transform ${duration}ms ease` : 'none';
    this.#track.style.transform = px ? `translate3d(${px}px, 0, 0)` : '';
  }

  /** transition の終わりを待つ。transitionend が来ない場合に備えて時間でも打ち切る。 */
  #afterTransition() {
    const duration = this.#duration;
    if (!duration) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.#track.removeEventListener('transitionend', onEnd);
        resolve(undefined);
      };
      /** @param {TransitionEvent} event */
      const onEnd = (event) => {
        if (event.target === this.#track && event.propertyName === 'transform') finish();
      };
      this.#track.addEventListener('transitionend', onEnd);
      setTimeout(finish, duration + 150);
    });
  }

  /** @param {number} times */
  #rotateForward(times) {
    for (let i = 0; i < times; i++) {
      const first = this.#track.firstElementChild;
      if (first) this.#track.append(first);
    }
  }

  /** @param {number} times */
  #rotateBackward(times) {
    for (let i = 0; i < times; i++) {
      const last = this.#track.lastElementChild;
      if (last) this.#track.prepend(last);
    }
  }

  /**
   * @param {number} delta 正で次へ、負で前へ。
   * @param {number} [fromOffset] ドラッグ中の現在位置（px）。そこから動かし始める。
   */
  async #move(delta, fromOffset = 0) {
    if (this.#busy || !delta) return;
    this.#busy = true;
    try {
      const step = this.#step();
      if (delta > 0) {
        if (fromOffset) this.#setOffset(fromOffset, false);
        void this.#track.offsetWidth;
        this.#setOffset(-step * delta, true);
        await this.#afterTransition();
        this.#setOffset(0, false);
        this.#rotateForward(delta);
      } else {
        const times = -delta;
        this.#rotateBackward(times);
        this.#setOffset(-step * times + fromOffset, false);
        void this.#track.offsetWidth;
        this.#setOffset(0, true);
        await this.#afterTransition();
        this.#setOffset(0, false);
      }
      void this.#track.offsetWidth;
    } finally {
      this.#busy = false;
      this.#update();
    }
  }

  /** ドラッグを離したが閾値に届かなかったとき、元の位置へ戻す。 */
  async #snapBack(fromOffset) {
    if (this.#busy) return;
    this.#busy = true;
    try {
      this.#setOffset(fromOffset, false);
      void this.#track.offsetWidth;
      this.#setOffset(0, true);
      await this.#afterTransition();
      this.#setOffset(0, false);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * 左右の端まで常に埋まるよう、控えの枚数を決めて足りない分だけ一式を複製し、
   * 現在のスライドが画面の中心に来るようトラックをずらす。画面幅が変わるたびに呼ぶ。
   */
  #fill() {
    const current = this.current;
    for (const clone of this.#track.querySelectorAll('[data-loop-clone]')) clone.remove();

    // 複製を外すと原本だけが巡回順のまま残る。いまのスライドを先頭に戻してから、
    // その並び順のまま一式を複製する（一式単位で並ぶので周期は崩れない）
    this.#lead = 0;
    for (let i = 0; i < this.#count && this.current !== current; i++) this.#rotateForward(1);

    this.#track.style.marginInlineStart = '0px';
    const step = this.#step();
    const width = this.#track.firstElementChild?.getBoundingClientRect().width || 0;
    const viewportWidth = this.#viewport.clientWidth;
    if (step <= 0 || width <= 0) return;

    // 中心の1枚の左右に見える幅。そこを埋める枚数 + 動いている間に入ってくる分
    const side = Math.max(0, (viewportWidth - width) / 2);
    const perSide = Math.ceil(side / step);
    const maxJump = Math.floor(this.#count / 2); // ドットで一度に動く最大枚数
    const lead = perSide + maxJump;
    const needed = lead + 1 + perSide + maxJump;

    const originals = this.#realSlides();
    let guard = 0;
    while (this.#track.children.length < needed && guard++ < 20) {
      for (const slide of originals) {
        const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
        clone.setAttribute('data-loop-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('aria-label');
        clone.removeAttribute('ref');
        // 画面に見えるのでクリックは生かす。読み上げとキーボード移動の対象からだけ外す
        for (const el of clone.querySelectorAll('a, button, [tabindex]')) el.setAttribute('tabindex', '-1');
        for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
        this.#track.append(clone);
      }
    }

    // 末尾から lead 枚を先頭へ回すと、いまのスライドが lead 番目に来る
    this.#rotateBackward(lead);
    this.#lead = lead;

    // lead 番目の子の中心 = 画面の中心
    this.#track.style.marginInlineStart = `${Math.round(viewportWidth / 2 - width / 2 - lead * step)}px`;
    this.#lastWidth = viewportWidth;
  }

  #update() {
    const current = this.current;
    this.#dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', String(index === current));
    });
  }

  /* ---------- 自動再生 ---------- */

  #restartTimer() {
    clearInterval(this.#timer);
    this.#timer = undefined;
    const interval = this.#interval;
    if (!interval || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.#timer = window.setInterval(() => {
      if (this.#paused || this.#hovered || this.#focused || !this.#inView || document.hidden) return;
      if (this.#drag) return;
      this.next();
    }, interval);
  }

  #onVisibilityChange = () => {
    if (!document.hidden) this.#restartTimer();
  };

  /* ---------- 操作 ---------- */

  #bind() {
    this.querySelector('[data-loop-prev]')?.addEventListener('click', () => {
      this.previous();
      this.#restartTimer();
    });
    this.querySelector('[data-loop-next]')?.addEventListener('click', () => {
      this.next();
      this.#restartTimer();
    });
    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.#restartTimer();
      });
    });
    this.querySelector('[data-loop-pause]')?.addEventListener('click', () => this.pause());
    this.querySelector('[data-loop-play]')?.addEventListener('click', () => this.play());

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.#hovered = true;
    });
    this.addEventListener('pointerleave', () => {
      this.#hovered = false;
    });
    this.addEventListener('focusin', () => {
      this.#focused = this.matches(':focus-visible, :has(:focus-visible)');
    });
    this.addEventListener('focusout', () => {
      this.#focused = false;
    });

    this.#bindSwipe();

    this.#resizeObserver = new ResizeObserver(() => {
      const width = this.#viewport.clientWidth;
      if (width === this.#lastWidth || this.#busy) return;
      this.#fill();
      this.#update();
    });
    this.#resizeObserver.observe(this.#viewport);

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
    });
    this.#intersectionObserver.observe(this);

    document.addEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #bindSwipe() {
    const viewport = this.#viewport;

    viewport.addEventListener('pointerdown', (event) => {
      if (this.#busy || event.button !== 0) return;
      this.#drag = { x: event.clientX, y: event.clientY, dx: 0, id: event.pointerId, horizontal: null };
      this.#suppressClick = false;
    });

    viewport.addEventListener('pointermove', (event) => {
      const drag = this.#drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (drag.horizontal === null) {
        if (Math.abs(dx) < DRAG_CLICK_TOLERANCE && Math.abs(dy) < DRAG_CLICK_TOLERANCE) return;
        drag.horizontal = Math.abs(dx) > Math.abs(dy);
        if (!drag.horizontal) {
          this.#drag = null;
          return;
        }
        viewport.setPointerCapture(event.pointerId);
      }
      drag.dx = dx;
      this.#suppressClick = true;
      // 左右とも控えのスライドが並んでいるので、指に合わせてずらすだけでよい
      this.#setOffset(dx, false);
    });

    const end = (/** @type {PointerEvent} */ event) => {
      const drag = this.#drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.#drag = null;
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);

      const dx = event.type === 'pointercancel' ? 0 : drag.dx;

      if (dx <= -SWIPE_THRESHOLD) this.#move(1, dx);
      else if (dx >= SWIPE_THRESHOLD) this.#move(-1, dx);
      else if (dx) this.#snapBack(dx);
      else this.#setOffset(0, false);

      this.#restartTimer();
    };
    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);

    // ドラッグした後のクリックでリンクへ飛ばない
    viewport.addEventListener(
      'click',
      (event) => {
        if (!this.#suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.#suppressClick = false;
      },
      true
    );
    // 画像のドラッグ（ゴースト画像）を止める
    viewport.addEventListener('dragstart', (event) => event.preventDefault());
  }
}

if (!customElements.get('loop-carousel')) {
  customElements.define('loop-carousel', LoopCarousel);
}
